import * as React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type FeedbackType = "idea" | "problem" | "question";

export interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectName: string | null;
  pageContext: string;
}

const feedbackTypes: { value: FeedbackType; label: string }[] = [
  { value: "idea", label: "Idea" },
  { value: "problem", label: "Problem" },
  { value: "question", label: "Question" },
];

/**
 * Dialog for submitting user feedback.
 * Strictly limits telemetry: includes only current page and project names in context.
 * Never collects or sends run content, secrets, credentials, or other page data.
 * When no backend submission endpoint is configured, presents an honest unavailable state.
 */
export function FeedbackDialog({
  open,
  onOpenChange,
  projectName,
  pageContext,
}: FeedbackDialogProps) {
  const [type, setType] = React.useState<FeedbackType>("idea");
  const categoryRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  /** Arrow keys move the selection the way a native radio group does, and follow it with focus. */
  function onCategoryKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const current = feedbackTypes.findIndex((option) => option.value === type);
    const next = (current + step + feedbackTypes.length) % feedbackTypes.length;
    const option = feedbackTypes[next];
    if (!option) return;
    setType(option.value);
    categoryRefs.current[next]?.focus();
  }
  const [message, setMessage] = React.useState("");
  const [status, setStatus] = React.useState<"idle" | "unavailable">("idle");
  const [prevOpen, setPrevOpen] = React.useState(open);

  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setMessage("");
      setStatus("idle");
      setType("idea");
    }
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!message.trim()) return;

    // No submission endpoint exists in the console API. Present an honest unavailable state.
    setStatus("unavailable");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <DialogTitle>Submit feedback</DialogTitle>
            <DialogDescription>
              Tell us what to fix or build. Feedback is not submitted from this console yet.
            </DialogDescription>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">
                Feedback category
              </label>
              <div
                role="radiogroup"
                aria-label="Feedback category"
                className="flex rounded-md bg-muted p-0.5"
                onKeyDown={onCategoryKeyDown}
              >
                {feedbackTypes.map((option, index) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={type === option.value}
                    // Roving tabindex: one Tab stop for the group, arrows move within it. Without
                    // this every option was a separate stop with no way to change the selection
                    // from the keyboard.
                    tabIndex={type === option.value ? 0 : -1}
                    ref={(element) => {
                      categoryRefs.current[index] = element;
                    }}
                    onClick={() => setType(option.value)}
                    className={cn(
                      "flex h-11 flex-1 items-center justify-center rounded-sm px-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors md:h-7",
                      type === option.value && "bg-card text-foreground shadow-2xs",
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="feedback-message"
                className="text-xs font-medium text-muted-foreground block"
              >
                Your message
              </label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="What happened, or what would help?"
                rows={4}
                required
                className="resize-none"
              />
            </div>

            <div
              className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1"
              data-testid="feedback-context-boundary"
            >
              <div className="flex items-center justify-between font-medium text-foreground">
                <span>Submission context</span>
                <span className="text-xs font-mono text-muted-foreground">Safe metadata only</span>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1 font-mono text-xs">
                <div>
                  <span className="text-muted-foreground">Project: </span>
                  <span className="font-semibold text-foreground">{projectName ?? "None"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Page: </span>
                  <span className="font-semibold text-foreground">{pageContext || "Console"}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground/90 pt-1 border-t border-border/40">
                Only the page and project names are included. No run content, secrets, credentials,
                or other page data are ever collected or sent.
              </p>
            </div>

            {status === "unavailable" && (
              <Alert variant="destructive" role="alert">
                <AlertTitle>Feedback service unavailable</AlertTitle>
                <AlertDescription>
                  Feedback submission is not available yet. No submission endpoint is configured in
                  this console deployment.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Send feedback</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
